// The app-relative path rule — ONE definition for every surface that hands the
// PWA somewhere to navigate: a `NotificationEvent.deepLink`, the Web Push
// payload's `url`, anything that later reaches `client.navigate()` /
// `clients.openWindow()`.
//
// "App-relative" means EXACTLY one leading slash. `//evil.example` (protocol
// relative) and `/\evil.example` (backslash) are both resolved OFF-ORIGIN by
// browsers, so a value that can steer a focused tab must be rejected unless it
// can only ever land on our own origin.
//
// `apps/dashboard/public/sw.js` re-states this predicate — it is plain JS served
// verbatim from `public/` (no build step), so it cannot import. Keep the two in
// step; the dashboard's `sw.contract.test.ts` asserts the re-statement.

import { z } from 'zod';

/** Max length accepted anywhere a deep link is stored or sent. */
export const APP_PATH_MAX_LENGTH = 2048;

/** Exactly one leading slash — rejects protocol-relative + backslash forms. */
export function isAppRelativePath(value: string): boolean {
  return value === '/' || /^\/(?![/\\])/.test(value);
}

/** A non-empty, bounded, app-relative path. */
export const AppRelativePathSchema = z
  .string()
  .min(1)
  .max(APP_PATH_MAX_LENGTH)
  .refine(isAppRelativePath, 'must be an app-relative path (single leading "/")');
